/**
 * Assist profiles (V1-E11-T04): how a tenant's human agents are helped, as a YAML document: the knowledge searched
 * for what the customer says, a next step, what the agent must say, the analyses run afterwards. A SIPREC trunk
 * (Trunks) or a Call Script's `transcribe` names one. The API's problems are shown with their paths.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { parse, stringify } from "yaml";
import { ApiError, data, type Schemas } from "../api/client";
import { Section, type useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Profile = Schemas["ProfileOut"];
type Listed = ReturnType<typeof useApiQuery<{ data: Profile[] }>>;

const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

export const TEMPLATE = `kind: AssistProfile
metadata:
  name: support-desk
spec:
  language: en-US
  instructions: Our agents answer customers' questions about their orders and refunds.
  # knowledge:                     # passages suggested when the customer speaks
  #   collections: [policies]
  #   top_k: 2
  next_action: { enabled: true }   # a next step, from the tenant's copilot model
  nudges:
    - name: verify
      say: Can you confirm your order number?
      within_turns: 3
  post_call:                       # every call reviewed afterwards
    summary: true
    qa:
      items:
        - name: greeting
          question: Did the agent greet the customer and give their name?
`;

function Editor({ profile, onDone }: { profile: Profile | null; onDone: () => void }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const [text, setText] = useState(() => (profile ? stringify(profile.spec) : TEMPLATE));
  let doc: unknown = null;
  let broken: string | null = null;
  try {
    doc = parse(text);
  } catch (e) {
    broken = e instanceof Error ? e.message.split("\n")[0] ?? "" : String(e);
  }
  const save = useMutation({
    mutationFn: () => {
      const body = doc as Schemas["AssistProfileDocument"];
      return profile
        ? data(api.PUT("/v1/assist-profiles/{assist_profile_id}", { params: { path: { assist_profile_id: profile.id } }, body }))
        : data(api.POST("/v1/assist-profiles", { body }));
    },
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ["assist-profiles", tenant] });
      onDone();
    },
  });
  const problems = save.error instanceof ApiError ? save.error.problem.errors ?? [] : [];
  return (
    <form aria-label={profile ? t("assist.profiles.editOne", { name: profile.name }) : t("assist.profiles.new")}
      className="space-y-2 rounded-lg border border-[var(--color-border)] p-3"
      onSubmit={(e: FormEvent) => { e.preventDefault(); if (!broken) save.mutate(); }}>
      <label htmlFor={`${id}-yaml`} className="block text-sm font-medium">{t("assist.profiles.yaml")}</label>
      <textarea id={`${id}-yaml`} rows={20} spellCheck={false} className={`${field} font-mono text-xs`} value={text}
        aria-invalid={broken !== null || problems.length > 0 || undefined} aria-describedby={`${id}-hint`}
        onChange={(e) => setText(e.target.value)} />
      <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">
        {broken ? t("assist.profiles.yamlBroken", { why: broken }) : t("assist.profiles.yamlHint")}
      </p>
      {problems.length > 0 ? (
        <div role="alert" className="rounded-md border border-[var(--color-danger)] p-3 text-sm">
          <p className="font-medium">{t("assist.profiles.refused")}</p>
          <ul className="mt-1 list-disc pl-5">
            {problems.map((p) => (
              <li key={`${p.field}:${p.message}`}><span className="font-mono">{p.field}</span>: {p.message}</li>
            ))}
          </ul>
        </div>
      ) : save.isError && <ErrorState error={save.error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={broken !== null || save.isPending}>{t("assist.profiles.save")}</Button>
        <Button type="button" variant="ghost" onClick={onDone}>{t("common.cancel")}</Button>
      </div>
    </form>
  );
}

function Remove({ profile }: { profile: Profile }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const remove = useMutation({
    mutationFn: () => data(api.DELETE("/v1/assist-profiles/{assist_profile_id}", { params: { path: { assist_profile_id: profile.id } } })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["assist-profiles", tenant] }),
  });
  return (
    <Button variant="ghost" aria-label={t("assist.profiles.remove", { name: profile.name })} disabled={remove.isPending}
      onClick={() => { if (window.confirm(t("assist.profiles.removeConfirm", { name: profile.name }))) remove.mutate(); }}>
      <Trash2 size={16} aria-hidden="true" />
    </Button>
  );
}

export function Profiles({ query }: { query: Listed }) {
  const t = useT();
  const format = useFormat();
  const canWrite = useCan("agents:write");
  const [editing, setEditing] = useState<Profile | "new" | null>(null);
  return (
    <section aria-labelledby="assist-profiles" className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 id="assist-profiles" className="text-lg font-semibold">{t("assist.profiles")}</h2>
        {canWrite && editing === null && (
          <Button variant="secondary" className="ml-auto" onClick={() => setEditing("new")}>
            <Plus size={16} aria-hidden="true" />{t("assist.profiles.new")}
          </Button>
        )}
      </div>
      <p className="text-sm text-[var(--color-muted)]">{t("assist.profiles.intro")}</p>
      {editing !== null && (
        <Editor key={editing === "new" ? "new" : editing.id} profile={editing === "new" ? null : editing} onDone={() => setEditing(null)} />
      )}
      <Section query={query}>
        {(page) =>
          page.data.length === 0 ? (
            editing === null && <Empty title={t("assist.profiles.empty")}><p>{t("assist.profiles.emptyBody")}</p></Empty>
          ) : (
            <Table caption={t("assist.profiles")} rows={page.data} rowKey={(p) => p.id}
              columns={[
                { header: t("assist.profiles.name"), cell: (p) => <span className="font-medium">{p.name}</span> },
                { header: t("assist.profiles.knowledge"), cell: (p) => p.spec.spec.knowledge?.collections.join(", ") ?? "—" },
                { header: t("assist.profiles.nudges"), cell: (p) => String(p.spec.spec.nudges?.length ?? 0) },
                { header: t("assist.profiles.updated"), cell: (p) => format.dateTime(p.updated_at) },
                {
                  header: t("assist.profiles.actions"),
                  cell: (p) => canWrite ? (
                    <span className="flex gap-1">
                      <Button variant="secondary" aria-label={t("assist.profiles.editOne", { name: p.name })} onClick={() => setEditing(p)}>
                        {t("assist.profiles.edit")}
                      </Button>
                      <Remove profile={p} />
                    </span>
                  ) : "—",
                },
              ]}
            />
          )
        }
      </Section>
    </section>
  );
}
